import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { io, Socket } from "socket.io-client";
import { toast } from "react-toastify";
import SectionRenderer from "./PresentationComponents/SectionRenderer";
import { OutlineItem, GeneratedDataItem } from "./PresentationComponents/types";
import { debounce, throttle, cleanupSocket, chunkData, monitorMemoryUsage } from "../../utils/memoryOptimization";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5001";;
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5001';


interface DynamicGeneratedDataViewerProps {
  documentID: string;
  serverUrl?: string;
  initialSelectedOutlineID?: string;
  editable?: boolean;
  outlineData: any;
  onOutlineChange?: (outlineID: string) => void;
  handleRefineClick?: (text?: string) => void; // Updated to accept text parameter
  onAddNewOutline?: (title: string, afterOutlineID: string) => void;
  onDeleteOutline?: (outlineID: string, type: string) => void;
  onRefreshOutlines?: () => void;

  onDocumentNameChange?: (documentName: string) => void; // New prop to pass document name to parent

  idx?: number;

}

const DynamicGeneratedDataViewer: React.FC<DynamicGeneratedDataViewerProps> = ({
  documentID,
  serverUrl = SOCKET_URL,
  initialSelectedOutlineID = "",
  editable = true,
  outlineData,
  onOutlineChange,
  handleRefineClick,
  onAddNewOutline,
  onDeleteOutline,
  onRefreshOutlines,
  onDocumentNameChange,
  idx,
}) => {
  // State for all outline sections
  const [outline, setOutline] = useState<OutlineItem[]>([]);

  // Helper to refresh outlines after add/delete
  const refreshOutlines = useCallback(async () => {
    if (!documentID) return;
    setLoadingOutline(true);
    const authToken = sessionStorage.getItem("authToken");
    try {
      const response = await axios.get(
        `${API_URL}/api/v1/outline/${documentID}/documentGenerationOutline`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      setOutline(response.data.outline || []);
    } catch (err) {
      console.error("Failed to refresh outline:", err);
    } finally {
      setLoadingOutline(false);
    }
  }, [documentID]);
  // When a new outline is added, refresh outlines
  useEffect(() => {
    if (typeof onAddNewOutline === 'function') {
      // Wrap the original handler to refresh after add
      const originalAdd = onAddNewOutline;
      (window as any).__dynamicAddOutlineHandler = async (
        title: string,
        afterOutlineID: string
      ) => {
        await originalAdd(title, afterOutlineID);
        await refreshOutlines();
      };
    }
  }, [onAddNewOutline, refreshOutlines]);

  // If parent calls onAddNewOutline, use our wrapped version
  const handleAddNewOutline = useCallback(
    async (title: string, afterOutlineID: string) => {
      if (typeof onAddNewOutline === 'function') {
        await onAddNewOutline(title, afterOutlineID);
        await refreshOutlines();
      }
    },
    [onAddNewOutline, refreshOutlines]
  );

  // Pass handleAddNewOutline to children/components instead of onAddNewOutline

  // Currently selected outline section (used for sidebar highlighting)
  const [selectedOutlineID, setSelectedOutlineID] = useState<string>(
    initialSelectedOutlineID
  );

  // All generated data for all sections
  const [generatedData, setGeneratedData] = useState<GeneratedDataItem[]>([]);

  
  // Document name state
  const [documentName, setDocumentName] = useState<string>('');
  const [loadingDocumentName, setLoadingDocumentName] = useState<boolean>(true);
  
  // Loading and error states
  const [loadingOutline, setLoadingOutline] = useState<boolean>(true);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Socket connection
  const [socket, setSocket] = useState<Socket | null>(null);
  // Track socket connection state
  const [, setIsConnected] = useState<boolean>(false);

  // Edit mode states
  const [editMode, setEditMode] = useState<Record<string, boolean>>({});
  const [editData, setEditData] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});


  useEffect(() => {
    // Handle postMessage events
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "forceViewMode" && event.data.componentId) {
        // Force the component into view mode
        setEditMode(prev => ({ ...prev, [event.data.componentId]: false }));
        // Also clear any pending saving status
        setSaving(prev => {
          const copy = { ...prev };
          delete copy[event.data.componentId];
          return copy;
        });
      }
    };
    
    // Handle custom DOM events
    const handleCloseEditMode = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.componentId) {
        // Force the component into view mode
        setEditMode(prev => ({ ...prev, [customEvent.detail.componentId]: false }));
        // Also clear any pending saving status
        setSaving(prev => {
          const copy = { ...prev };
          delete copy[customEvent.detail.componentId];
          return copy;
        });
      }
    };

    window.addEventListener("message", handleMessage);
    window.addEventListener("closeEditMode", handleCloseEditMode as EventListener);
    
    return () => {
      window.removeEventListener("message", handleMessage);
      window.removeEventListener("closeEditMode", handleCloseEditMode as EventListener);
    };
  }, []);
  // ...existing code...
  

  // References for the scroll container
  const dataListRef = useRef<HTMLDivElement>(null);
  const lastScrollPosition = useRef<number>(0);
  const isAddingComponent = useRef<boolean>(false);

  // Refs to hold latest outline and selection for use inside socket handlers
  const outlineRef = useRef<OutlineItem[]>(outline);
  const selectedOutlineIDRef = useRef<string>(selectedOutlineID);

  // Keep refs in sync with state
  useEffect(() => {
    outlineRef.current = outline;
  }, [outline]);
  useEffect(() => {
    selectedOutlineIDRef.current = selectedOutlineID;
  }, [selectedOutlineID]);

  // Observer for scroll tracking
  const [intersectionObserver, setIntersectionObserver] =
    useState<IntersectionObserver | null>(null);

  // Enhanced intersection observer for scroll spy with stability improvements
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visibleSections = entries.filter((entry) => entry.isIntersecting);
        if (visibleSections.length > 0) {
          visibleSections.sort(
            (a, b) => b.intersectionRatio - a.intersectionRatio
          );
          const mostVisible = visibleSections[0];
          const sectionId = mostVisible.target.getAttribute("data-section-id");
          
          // Enhanced debouncing with component addition detection
          if (sectionId && sectionId !== selectedOutlineID) {
            // Check if components are being added (prevent outline changes during additions)
            if (!isAddingComponent.current) {
              // Use longer timeout to prevent rapid changes during scroll
              setTimeout(() => {
                // Double-check that we're not adding components and section still exists
                if (!isAddingComponent.current && document.querySelector(`[data-section-id="${sectionId}"]`)) {
                  setSelectedOutlineID(sectionId);
                  if (onOutlineChange) {
                    onOutlineChange(sectionId);
                  }
                }
              }, 200); // Increased debounce time
            }
          }
        }
      },
      {
        root: dataListRef.current,
        rootMargin: "-15% 0px -15% 0px", // Increased margin for better stability
        threshold: [0.4, 0.6], // More conservative thresholds
      }
    );
    setIntersectionObserver(observer);
    return () => observer.disconnect();
  }, [onOutlineChange, selectedOutlineID]);

  useEffect(() => {
    if (intersectionObserver) {
      intersectionObserver.disconnect();
      document.querySelectorAll("[data-section-id]").forEach((section) => {
        intersectionObserver.observe(section);
      });
    }
    return () => intersectionObserver?.disconnect();
  }, [intersectionObserver]);

  // Enhanced scroll handler with component stability
  const handleScrollRaw = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (e.currentTarget && !isAddingComponent.current) {
      lastScrollPosition.current = e.currentTarget.scrollTop;
    }
  }, []);

  // Debounce the scroll handler with longer delay for stability
  const handleScroll = useCallback(
    debounce((e: React.UIEvent<HTMLDivElement>) => {
      if (!isAddingComponent.current) {
        handleScrollRaw(e);
      }
    }, 150), // Increased debounce time
    [handleScrollRaw]
  );

  // Fetch document name
  // const fetchDocumentName = useCallback(async () => {
  //   if (!documentID) return;
    
  //   setLoadingDocumentName(true);
  //   const authToken = sessionStorage.getItem('authToken');
    
  //   // Use REACT_APP_BACKEND_URL environment variable like other parts of the app
  //   const backendUrl = process.env.REACT_APP_BACKEND_URL || API_URL;
    
  //   try {
  //     const response = await axios.get(
  //       `${backendUrl}/api/v1/data/appscripts/document-name/${documentID}`,
  //       {
  //         headers: {
  //           Authorization: `Bearer ${authToken}`
  //         }
  //       }
  //     );
      
  //     if (response.data.success) {
  //       setDocumentName(response.data.documentName);
        
  //       // Notify parent component about document name change if callback provided
  //       if (onDocumentNameChange) {
  //         onDocumentNameChange(response.data.documentName);
  //       }
  //     }
  //   } catch (err) {
  //     console.error('Failed to fetch document name:', err);
  //     // Don't set error state for document name failure as it's not critical
  //   } finally {
  //     setLoadingDocumentName(false);
  //   }
  // }, [documentID, onDocumentNameChange]);

  // // Fetch document name on mount
  // useEffect(() => {
  //   fetchDocumentName();
  // }, [fetchDocumentName]);

  // Fetch outline on mount - skip for shared documents as parent handles this
  useEffect(() => {
    // Skip outline fetching for shared documents as parent component handles it
    if (!documentID || !editable) {
      setLoadingOutline(false);
      return;
    }
    
    setLoadingOutline(true);
    // Get authToken from sessionStorage
    const authToken = sessionStorage.getItem("authToken");

    axios
      .get(
        `${API_URL}/api/v1/outline/${documentID}/documentGenerationOutline`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      )
      .then((res) => {
        setOutline(res.data.outline || []);
        if (res.data.outline && res.data.outline.length > 0) {
          // Use initialSelectedOutlineID if provided and exists in the outline, otherwise use the first outline
          if (
            initialSelectedOutlineID &&
            res.data.outline.some(
              (item: OutlineItem) => item.outlineID === initialSelectedOutlineID
            )
          ) {
            setSelectedOutlineID(initialSelectedOutlineID);
          } else {
            setSelectedOutlineID(res.data.outline[0].outlineID);
          }
        }
        setLoadingOutline(false);
      })
      .catch((err) => {
        console.error("Failed to fetch outline:", err);
        setLoadingOutline(false);
      });
  }, [documentID, initialSelectedOutlineID, editable]);

  // Handle outline change: fetch data for the selected outline and the next one
  useEffect(() => {
    if (!selectedOutlineID || !socket || !socket.connected) return;

    // Only reset scroll position if this is a user-initiated outline change, not from intersection observer
    // Remove automatic scroll reset to prevent flickering when new components are added

    // Notify parent component about outline change
    if (onOutlineChange && selectedOutlineID) {
      onOutlineChange(selectedOutlineID);
    }

    // Fetch data for the new selection and the next outline
    const authToken = sessionStorage.getItem("authToken");

    // Find the current outline index
    const currentIndex = outline.findIndex(
      (item) => item.outlineID === selectedOutlineID
    );
    if (currentIndex === -1) return;

    // Only fetch if the data isn't already loaded
    const hasCurrentOutlineData = generatedData.some(
      (item) => item.outlineID === selectedOutlineID
    );

    // If data for this outline isn't already loaded, fetch it
    if (!hasCurrentOutlineData) {
      console.log(
        "Fetching data for newly selected outline:",
        selectedOutlineID
      );
      setLoadingData(true);

      // Get the current outline and the next one (if it exists)
      const outlinesToFetch = [outline[currentIndex]];
      if (currentIndex + 1 < outline.length) {
        outlinesToFetch.push(outline[currentIndex + 1]);
      }

      // Create a promise for each selected outline section to fetch its data
      const fetchPromises = outlinesToFetch.map((outlineItem) => {
        return new Promise<GeneratedDataItem[]>((resolve) => {
          // Join this outline room
          socket.emit("join-outline", outlineItem.outlineID);

          // Create a one-time listener for this specific outline's data
          const listener = (data: GeneratedDataItem[]) => {
            console.log(
              `Received data for outline ${outlineItem.title}:`,
              data
            );
            resolve(Array.isArray(data) ? data : []);
            // Remove this listener after receiving data
            socket.off(`generatedData:${outlineItem.outlineID}`, listener);
          };

          // Listen for data for this specific outline
          socket.on(`generatedData:${outlineItem.outlineID}`, listener);

          // Set a timeout in case the socket doesn't respond
          setTimeout(() => {
            console.log(`Timeout for outline ${outlineItem.title}`);
            socket.off(`generatedData:${outlineItem.outlineID}`, listener);
            resolve([]);
          }, 5000); // 5 second timeout

          // Request data for this outline
          socket.emit("fetchGeneratedData", {
            outlineID: outlineItem.outlineID,
            authToken,
          });
        });
      });

      // Once selected data is fetched, update the state
      Promise.all(fetchPromises).then((sectionData) => {
        // Flatten the array of arrays into a single array of data items
        const loadedData = sectionData.flat();
        setGeneratedData((prevData) => {
          // Combine with existing data, removing duplicates
          const existingIds = new Set(prevData.map((item) => item._id));
          const newData = loadedData.filter(
            (item) => !existingIds.has(item._id)
          );
          return [...prevData, ...newData];
        });
        setLoadingData(false);
      });
    }
  }, [selectedOutlineID, onOutlineChange, socket, outline, generatedData]);

  // Fetch data for active outline and the next one (desktop) or all outlines (mobile)
  useEffect(() => {
    // Create socket once per document (or when serverUrl changes). Use refs for outline/selection
    if (!documentID) return;
    setLoadingData(true);
    // Get authToken from sessionStorage for socket authentication
    const authToken = sessionStorage.getItem("authToken");

    // Connect socket with auth token and memory optimization
    const socketInstance = io(`${serverUrl}/generated-data`, {
      auth: {
        token: authToken,
      },
      // Add connection optimization
      forceNew: false,
      reconnection: true,
      timeout: 20000,
      // maxHttpBufferSize removed (not a valid option)
    });
    setSocket(socketInstance);

    // Monitor memory usage in development
    if (process.env.NODE_ENV === 'development') {
      monitorMemoryUsage();
    }

    socketInstance.on("connect", () => {
      console.log(
        "Socket connected, fetching active outline for document:",
        documentID
      );
      console.log("Socket id:", socketInstance.id);
      setIsConnected(true);
      // Only fetch data for the active outline and the next one.
      // Use refs to read latest outline/selection without re-creating socket.
      const currentOutline = outlineRef.current || [];
      const currentSelected = selectedOutlineIDRef.current;

      if (currentOutline.length > 0) {
        // Find the current outline index
        const currentIndex = currentOutline.findIndex(
          (item) => item.outlineID === currentSelected
        );

        // If not found, fallback to fetching selectedOutline directly
        const outlinesToFetch: OutlineItem[] = [];
        if (currentIndex !== -1) {
          outlinesToFetch.push(currentOutline[currentIndex]);
          if (currentIndex + 1 < currentOutline.length) {
            outlinesToFetch.push(currentOutline[currentIndex + 1]);
          }
        } else if (currentSelected) {
          // fallback: try to fetch only selectedOutlineID
          outlinesToFetch.push({ outlineID: currentSelected, title: "" } as OutlineItem);
        }

        if (outlinesToFetch.length === 0) {
          // Nothing to fetch
          setLoadingData(false);
        } else {
          console.log(
            "Fetching data for outlines:",
            outlinesToFetch.map((o) => o.title || o.outlineID).join(", ")
          );

          const fetchPromises = outlinesToFetch.map((outlineItem) => {
            return new Promise<GeneratedDataItem[]>((resolve) => {
              socketInstance.emit("join-outline", outlineItem.outlineID);

              const listener = (data: GeneratedDataItem[]) => {
                resolve(Array.isArray(data) ? data : []);
                socketInstance.off(`generatedData:${outlineItem.outlineID}`, listener);
              };

              socketInstance.on(`generatedData:${outlineItem.outlineID}`, listener);

              setTimeout(() => {
                socketInstance.off(`generatedData:${outlineItem.outlineID}`, listener);
                resolve([]);
              }, 5000);

              socketInstance.emit("fetchGeneratedData", {
                outlineID: outlineItem.outlineID,
                authToken,
              });
            });
          });

          Promise.all(fetchPromises).then((sectionData) => {
            const loadedData = sectionData.flat();
            console.log("Data received for active sections:", loadedData.length);
            // Merge with existing data but prefer the freshly loaded data ordering
            setGeneratedData((prev) => {
              const existingIds = new Set(prev.map((p) => p._id));
              const newItems = loadedData.filter((i) => !existingIds.has(i._id));
              return [...prev, ...newItems];
            });
            setLoadingData(false);
          });
        }
      } else if (currentSelected) {
        // No outline list yet - request selected outline data directly
        socketInstance.emit("join-outline", currentSelected);
        socketInstance.emit("fetchGeneratedData", {
          outlineID: currentSelected,
          authToken,
        });
      } else {
        setLoadingData(false);
      }
    });

    socketInstance.on("disconnect", () => {
      setIsConnected(false);
    });

    socketInstance.on("generatedData", (data: GeneratedDataItem[]) => {
      console.log("Received generatedData event:", data?.length || 0, "items");

      // Process data in chunks to prevent memory spikes
      if (Array.isArray(data) && data.length > 0) {
        const chunks = chunkData(data, 20); // Process 20 items at a time
        
        chunks.forEach((chunk, index) => {
          setTimeout(() => {
            setGeneratedData((prev) => {
              // Filter out any duplicates and merge with existing data
              const existingIds = new Set(prev.map((item) => item._id));
              const newItems = chunk.filter((item) => !existingIds.has(item._id));
              const updatedData = [...prev, ...newItems];
              
              // Sort by creation time or position to maintain order
              return updatedData.sort((a, b) => {
                const aTime = new Date(a.createdAt || 0).getTime();
                const bTime = new Date(b.createdAt || 0).getTime();
                return aTime - bTime;
              });
            });
            
            // Set loading to false after processing last chunk
            if (index === chunks.length - 1) {
              setLoadingData(false);
            }
          }, index * 100); // Stagger processing
        });
      } else {
        setLoadingData(false);
      }
    });

    // Listen for save confirmations/broadcasts from server
    socketInstance.on(
      "dataSaved",
      (res: {
        success: boolean;
        document?: GeneratedDataItem;
        message?: string;
      }) => {
        console.log("socket dataSaved received", res);
        if (res?.success && res.document) {
          const updated = res.document;
          setGeneratedData((prev) => {
            const exists = prev.some((p) => p._id === updated._id);
            if (exists)
              return prev.map((p) => (p._id === updated._id ? updated : p));
            return [...prev, updated];
          });
          // Keep editData in sync and clear any saving flag for the updated document
          setEditData((prev) => ({ ...prev, [updated._id]: updated }));
          setSaving((prev) => {
            const copy = { ...prev };
            if (updated._id) delete copy[updated._id];
            return copy;
          });
        } else if (!res?.success) {
          toast.error(res?.message || "Failed to save on server");
        }
      }
    );

    socketInstance.on("error", (err: { message: string }) => {
      console.error("Socket error:", err);
      setError(err.message);
      setLoadingData(false);
    });

    // Additional debug handlers
    socketInstance.on("connect_error", (err: any) => {
      console.error("Socket connect_error:", err);
    });
    socketInstance.on("reconnect_attempt", (attempt: number) => {
      console.warn("Socket reconnect attempt", attempt);
    });

    // Listen for update confirmations
    socketInstance.on(
      "dataUpdated",
      (response: {
        success: boolean;
        message: string;
        data?: GeneratedDataItem;
      }) => {
        if (response.success) {
          toast.success(response.message || "Data updated successfully");

          // Update the local data with the new data if provided
          if (response.data) {
            setGeneratedData((prevData) =>
              prevData.map((item) =>
                item._id === response.data?._id ? response.data : item
              )
            );

            // Also update the edit data to keep it in sync
            if (response.data && response.data._id) {
              const updatedData = response.data;
              setEditData((prev) => ({
                ...prev,
                [updatedData._id]: updatedData,
              }));
            }
          }

          // Refresh the data from server to ensure we have the latest
          socketInstance.emit("fetchGeneratedData", {
            outlineID: selectedOutlineID,
            authToken: sessionStorage.getItem("authToken"),
          });
        } else {
          toast.error(response.message || "Failed to update data");
        }

        // Clear saving state for all components
        setSaving({});
      }
    );

    return () => {
      // Proper cleanup to prevent memory leaks
      cleanupSocket(socketInstance);

      // Clear any pending timeouts
      if (intersectionObserver) {
        intersectionObserver.disconnect();
      }

      // Reset component addition flag
      isAddingComponent.current = false;

      // Do not clear generatedData/editData here - keep cached data across socket reconnects
      // Monitor memory after cleanup
      if (process.env.NODE_ENV === 'development') {
        setTimeout(monitorMemoryUsage, 1000);
      }
    };
  }, [documentID, serverUrl]);

  // Toggle edit mode for a specific item with component addition tracking
  const toggleEditMode = useCallback(
    (id: string) => {
      // Set component addition flag when entering edit mode
      if (!editMode[id]) {
        isAddingComponent.current = true;
        setTimeout(() => {
          isAddingComponent.current = false;
        }, 1000);
      }
      
      setEditMode((prev) => {
        const newEditMode = { ...prev, [id]: !prev[id] };

        // Initialize edit data if entering edit mode
        if (!prev[id]) {
          const item = generatedData.find((item) => item._id === id);
          if (item) {
            // Create a new object for this item's edit data
            const editableData: GeneratedDataItem = {
              _id: item._id,
              documentID: item.documentID,
              outlineID: item.outlineID,
              component_id: item.component_id,
              component_type: item.component_type,
              userId: item.userId,
            };

            if (item.component_type === "Text") {
              const existingText: any =
                typeof item.text === "object" ? item.text : {};
              editableData.text = {
                title:
                  (existingText && typeof existingText.title === "string"
                    ? existingText.title
                    : item.text?.title) || "Text",
                text:
                  typeof item.text === "string"
                    ? item.text
                    : existingText && typeof existingText.text === "string"
                    ? existingText.text
                    : "",
                component_id:
                  item.component_id || existingText.component_id || "",
                citation: existingText.citation || "",
                colors: { ...(existingText.colors || {}) },
              };
            } else if (item.component_type === "People") {
              editableData.people = item.people
                ? { ...item.people }
                : undefined;
            } else if (item.component_type === "Table") {
              editableData.table = item.table
                ? {
                    ...item.table,
                    rows: item.table.rows ? [...item.table.rows] : [],
                  }
                : undefined;
              
              // Also initialize direct array format for TableComponent
              if (item.table) {
                // Transform backend format to direct arrays
                const columnHeaders: string[] = [];
                for (let i = 0; i <= 5; i++) {
                  const key = `columnHeader${i}` as keyof typeof item.table;
                  if ((item.table as any)[key] && (item.table as any)[key].trim() !== '') {
                    columnHeaders.push((item.table as any)[key]);
                  }
                }
                
                const rowHeaders: string[] = [];
                const rows: string[][] = [];
                for (let i = 1; i <= 8; i++) {
                  const rKey = `rowHeader${i}` as keyof typeof item.table;
                  const rowsKey = `rows${i}` as keyof typeof item.table;
                  
                  const hasRowHeader = (item.table as any)[rKey] && (item.table as any)[rKey].trim() !== '';
                  const rowData = (item.table as any)[rowsKey];
                  const hasRowData = rowData && Object.values(rowData).some((val: any) => val && val.toString().trim() !== '');
                  
                  if (hasRowHeader && hasRowData) {
                    rowHeaders.push((item.table as any)[rKey] || `Row ${i}`);
                    
                    const rowArr: string[] = [];
                    for (let j = 1; j <= 5; j++) {
                      rowArr.push((rowData && rowData[`attribute${j}`]) || '');
                    }
                    rows.push(rowArr);
                  }
                }
                
                // Ensure minimum table size
                const minColumnHeaders = Math.max(columnHeaders.length, 2);
                const minRowHeaders = Math.max(rowHeaders.length, 2);
                
                // Pad arrays to minimum size
                while (columnHeaders.length < minColumnHeaders) {
                  columnHeaders.push(`Column ${columnHeaders.length + 1}`);
                }
                while (rowHeaders.length < minRowHeaders) {
                  rowHeaders.push(`Row ${rowHeaders.length + 1}`);
                }
                while (rows.length < minRowHeaders) {
                  rows.push(Array(minColumnHeaders).fill(''));
                }
                
                // Ensure all rows have the correct number of columns
                rows.forEach(row => {
                  while (row.length < minColumnHeaders) {
                    row.push('');
                  }
                });
                

                
                // Store direct arrays for easier access by TableComponent
                editableData.columnHeaders = columnHeaders;
                editableData.rowHeaders = rowHeaders;
                editableData.rows = rows;
              } else {
                // Initialize with empty table if no table data exists
                editableData.columnHeaders = ['Column 1', 'Column 2'];
                editableData.rowHeaders = ['Row 1', 'Row 2'];
                editableData.rows = [['', ''], ['', '']];
              }
            } else if (item.component_type === "Chart") {
              console.log('toggleEditMode: Initializing chart edit data', { itemId: id, chartData: item.chart });
              editableData.chart = item.chart
                ? {
                    ...item.chart,
                  }
                : {
                    title: "",
          component_id: item.component_id || "",
                    chartType: "bar",
                    overview: "",
                    chart: {
                      title: "",
                      series1: { key: "", value1: "", value2: "", value3: "", value4: "", value5: "", value6: "", value7: "", value8: "", value9: "", value10: "" },
                      series2: { key: "", value1: "", value2: "", value3: "", value4: "", value5: "", value6: "", value7: "", value8: "", value9: "", value10: "" },
                      series3: { key: "", value1: "", value2: "", value3: "", value4: "", value5: "", value6: "", value7: "", value8: "", value9: "", value10: "" }
                    }
                  };
            } else if (item.component_type === "TextandImage") {
              editableData.textandimage = item.textandimage
                ? {
                    ...item.textandimage,
                    title: item.textandimage.title || "",
                    text: item.textandimage.text || "",
                    image: item.textandimage.image || "",
                    component_id: item.component_id || "",
                  }
                : {
                    title: "",
                    text: "",
                    image: "",
                    component_id: item.component_id || "",
                  };
            } else if (item.component_type === "Image") {
              editableData.image = item.image ? { ...item.image } : undefined;
            }

            // Set the edit data
            setEditData((prev) => ({
              ...prev,
              [id]: editableData,
            }));
          }
        }

        return newEditMode;
      });
    },
    [generatedData]
  );

  // Handle input changes in edit mode
  const handleInputChange = useCallback(
    (id: string, field: string, value: any, componentType?: string) => {
      setEditData((prev) => {
        const currentItem = { ...prev[id] };
        const ct = (componentType || "").toLowerCase();

        if (ct === "text") {
          // Ensure we have a text object with all required fields
          currentItem.text = {
            title: currentItem.text?.title || "Text",
            text: currentItem.text?.text || "",
            component_id: currentItem.text?.component_id || "",
            citation: currentItem.text?.citation || "",
            colors: currentItem.text?.colors || {},
            ...currentItem.text,
          };

          // Normalize the field path: if caller passed 'text.title' or 'text.text', strip the leading 'text.'
          const normalizedField = field.startsWith("text.")
            ? field.replace(/^text\./, "")
            : field;
          const nestedKeys = normalizedField.split(".");
          if (nestedKeys.length > 1) {
            let currentObj: any = currentItem.text;
            const lastKey = nestedKeys[nestedKeys.length - 1];

            // Navigate to the correct nested object
            for (let i = 0; i < nestedKeys.length - 1; i++) {
              const key = nestedKeys[i];
              currentObj[key] = currentObj[key] || {};
              currentObj = currentObj[key];
            }

            // Set the value
            currentObj[lastKey] = value;
          } else {
            currentItem.text[nestedKeys[0]] = value;
          }
        } else if (ct === "people") {
          currentItem.people = currentItem.people || {};
          
          // Handle nested fields for people components
          const normalizedField = field.startsWith("people.") ? field.replace(/^people\./, "") : field;
          const nestedKeys = normalizedField.split(".");
          if (nestedKeys.length > 1) {
            let currentObj: any = currentItem.people;
            const lastKey = nestedKeys[nestedKeys.length - 1];
            for (let i = 0; i < nestedKeys.length - 1; i++) {
              const key = nestedKeys[i];
              currentObj[key] = currentObj[key] || {};
              currentObj = currentObj[key];
            }
            currentObj[lastKey] = value;
          } else {
            currentItem.people[normalizedField] = value;
          }
        } else if (ct === "table") {
          currentItem.table = currentItem.table || {};

          // Handle table-specific fields that come directly from TableComponent
          if (field === "columnHeaders" || field === "rowHeaders" || field === "rows" || field === "title" || field === "table.title") {

            
            if (field === "table.title") {
              // Handle nested title field
              currentItem.table.title = value;
            } else {
              // Store the direct array/value format for easier handling
              (currentItem as any)[field] = value;
              // Also store in table object for consistency
              (currentItem.table as any)[field] = value;
            }
          } else {
            // Handle nested fields for table components
            const nestedKeys = field.split(".");
            if (nestedKeys.length > 1) {
              let currentObj: any = currentItem.table;
              for (let i = 0; i < nestedKeys.length - 1; i++) {
                currentObj[nestedKeys[i]] = currentObj[nestedKeys[i]] || {};
                currentObj = currentObj[nestedKeys[i]];
              }
              currentObj[nestedKeys[nestedKeys.length - 1]] = value;
            } else {
              (currentItem.table as any)[field] = value;
            }
          }
        } else if (ct === "chart") {
          console.log('handleInputChange: Processing chart field', { id, field, value, ct });
          currentItem.chart = currentItem.chart || {};

          // Handle nested fields for chart components using dot paths
          const nestedKeys = field.split(".");
          if (nestedKeys.length > 1) {
            let currentObj: any = currentItem.chart;
            // If first key is 'chart', skip it
            let startIndex = 0;
            if (nestedKeys[0] === "chart") startIndex = 1;
            for (let i = startIndex; i < nestedKeys.length - 1; i++) {
              currentObj[nestedKeys[i]] = currentObj[nestedKeys[i]] || {};
              currentObj = currentObj[nestedKeys[i]];
            }
            currentObj[nestedKeys[nestedKeys.length - 1]] = value;
          } else {
            (currentItem.chart as any)[field] = value;
          }
        } else if (ct === "textandimage") {
          currentItem.textandimage = currentItem.textandimage || {};

          // Normalize similar to text: if caller passed 'textandimage.image' -> strip prefix
          const normalizedFieldTI = field.startsWith("textandimage.")
            ? field.replace(/^textandimage\./, "")
            : field;
          const nestedKeysTI = normalizedFieldTI.split(".");
          if (nestedKeysTI.length > 1) {
            let currentObj: any = currentItem.textandimage;
            const lastKey = nestedKeysTI[nestedKeysTI.length - 1];
            for (let i = 0; i < nestedKeysTI.length - 1; i++) {
              const key = nestedKeysTI[i];
              currentObj[key] = currentObj[key] || {};
              currentObj = currentObj[key];
            }
            currentObj[lastKey] = value;
          } else {
            (currentItem.textandimage as any)[nestedKeysTI[0]] = value;
          }
        } else if (ct === "image") {
          currentItem.image = currentItem.image || {};

          // Handle nested fields for image components
          const normalizedField = field.startsWith("image.")
            ? field.replace(/^image\./, "")
            : field;
          const nestedKeys = normalizedField.split(".");
          if (nestedKeys.length > 1) {
            let currentObj: any = currentItem.image;
            const lastKey = nestedKeys[nestedKeys.length - 1];
            for (let i = 0; i < nestedKeys.length - 1; i++) {
              const key = nestedKeys[i];
              currentObj[key] = currentObj[key] || {};
              currentObj = currentObj[key];
            }
            currentObj[lastKey] = value;
          } else {
            if (normalizedField === "image") {
              // Handle the case where field is just "image"
              currentItem.image = value;
            } else {
              currentItem.image[normalizedField] = value;
            }
          }
        } else {
          (currentItem as any)[field] = value;
        }

        return { ...prev, [id]: currentItem };
      });
    },
    []
  );

  // Save changes to edited items with component stability
  const saveChanges = useCallback(
    (id: string) => {
      // Set component addition flag during save to prevent flickering
      isAddingComponent.current = true;
      
      // Set saving state for this item
      setSaving((prev) => ({ ...prev, [id]: true }));
      
      // Reset component addition flag after save completes
      setTimeout(() => {
        isAddingComponent.current = false;
      }, 1500);

      // Get the original and edited items
      const originalItem = generatedData.find((item) => item._id === id);
      const itemToUpdate = editData[id];

      console.log("saveChanges called for id=", id, {
        socketConnected: !!socket?.connected,
        originalItem,
        itemToUpdate,
      });

      // Validate presence of original and edited data first
      if (!originalItem) {
        console.warn("saveChanges: original item not found for id", id);
        toast.error("Unable to save: original item not found");
        setSaving((prev) => {
          const copy = { ...prev };
          delete copy[id];
          return copy;
        });
        return;
      }

      if (!itemToUpdate) {
        console.warn("saveChanges: no edited data present for id", id);
        // Don't show socket error - just clear saving state
        setSaving((prev) => {
          const copy = { ...prev };
          delete copy[id];
          return copy;
        });
        return;
      }

      // Get authToken for authentication and prepare formattedData so reconnect branch can reuse it
      const authToken = sessionStorage.getItem("authToken");

      // Create base data structure
      const baseData = {
        _id: originalItem._id,
        documentID: originalItem.documentID,
        outlineID: originalItem.outlineID,
        component_id: originalItem.component_id,
        component_type: originalItem.component_type,
        userId: originalItem.userId,
        method: "general",
      };

      // Format data according to component type
      let formattedData;
      if (originalItem.component_type === "Text") {
        formattedData = {
          ...baseData,
          text: {
            title:
              itemToUpdate.text?.title || originalItem.text?.title || "Text",
            text: itemToUpdate.text?.text || originalItem.text?.text || "",
            component_id: originalItem.component_id,
            citation:
              itemToUpdate.text?.citation || originalItem.text?.citation || "",
            colors:
              itemToUpdate.text?.colors || originalItem.text?.colors || {},
          },
        };
      } else if (originalItem.component_type === "Table") {
        // Transform table data from direct array format to backend format

        
        const tableData: any = {
          title: itemToUpdate.table?.title || itemToUpdate.title || originalItem.table?.title || "",
          component_id: originalItem.component_id,
          citation: itemToUpdate.table?.citation || originalItem.table?.citation || "",
          colors: itemToUpdate.table?.colors || originalItem.table?.colors || {},
        };

        // Handle columnHeaders - convert array to individual properties
        const columnHeaders = itemToUpdate.table?.columnHeaders || itemToUpdate.columnHeaders || [];

        for (let i = 0; i < Math.min(columnHeaders.length, 5); i++) {
          tableData[`columnHeader${i + 1}`] = columnHeaders[i] || "";
        }

        // Handle rowHeaders - convert array to individual properties
        const rowHeaders = itemToUpdate.table?.rowHeaders || itemToUpdate.rowHeaders || [];

        for (let i = 0; i < Math.min(rowHeaders.length, 8); i++) {
          tableData[`rowHeader${i + 1}`] = rowHeaders[i] || "";
        }

        // Handle rows - convert 2D array to individual row objects
        const rows = itemToUpdate.table?.rows || itemToUpdate.rows || [];

        for (let i = 0; i < Math.min(rows.length, 8); i++) {
          const rowData: any = {};
          const row = rows[i] || [];
          for (let j = 0; j < Math.min(row.length, 5); j++) {
            rowData[`attribute${j + 1}`] = row[j] || "";
          }
          tableData[`rows${i + 1}`] = rowData;
        }


        formattedData = {
          ...baseData,
          table: tableData,
        };
      } else if (originalItem.component_type === "Chart") {
        // Handle chart data properly
        const chartData: any = {
          title: itemToUpdate.chart?.title || originalItem.chart?.title || "",
          component_id: originalItem.component_id,
          chartType: itemToUpdate.chart?.chartType || originalItem.chart?.chartType || "bar",
          overview: itemToUpdate.chart?.overview || originalItem.chart?.overview || "",
          citation: itemToUpdate.chart?.citation || originalItem.chart?.citation || "",
          colors: itemToUpdate.chart?.colors || originalItem.chart?.colors || {},
          chart: {
            title: itemToUpdate.chart?.chart?.title || originalItem.chart?.chart?.title || "",
            ...((itemToUpdate.chart?.chart || originalItem.chart?.chart) || {})
          }
        };

        // Ensure chart object has the proper structure
        if (itemToUpdate.chart?.chart) {
          chartData.chart = {
            ...chartData.chart,
            ...itemToUpdate.chart.chart
          };
        }

        formattedData = {
          ...baseData,
          chart: chartData,
        };
      } else {
        formattedData = {
          ...baseData,
          ...itemToUpdate,
        };
      }

      console.log(
        "Saving formatted data (emitting saveGeneratedData):",
        formattedData
      );
      
      // Add specific logging for chart data
      if (originalItem.component_type === "Chart") {
        console.log("Chart save details:", {
          itemId: id,
          originalChart: originalItem.chart,
          editDataChart: itemToUpdate.chart,
          formattedChart: formattedData.chart,
          fullItemToUpdate: itemToUpdate
        });
      }

      if (socket && (socket as any).connected) {
        // Emit saveGeneratedData (upsert-by-component_id) and wait for ack if server supports it
        try {
          const payload = {
            outlineID: originalItem.outlineID,
            data: formattedData,
            authToken,
          };

          socket.emit("saveGeneratedData", payload, (ack: any) => {
            console.log("saveGeneratedData ack for", id, ack);
            if (ack && ack.success) {
              toast.success(ack.message || "Data saved successfully");

              if (ack.data) {
                const updated = ack.data;
                setGeneratedData((prevData) =>
                  prevData.map((d) => (d._id === updated._id ? updated : d))
                );
                setEditData((prev) => ({ ...prev, [updated._id]: updated }));

                // clear saving state for both old and new ids (in case server changed id)
                setSaving((prev) => {
                  const copy = { ...prev };
                  delete copy[id];
                  if (updated._id && updated._id !== id)
                    delete copy[updated._id];
                  return copy;
                });

                // ensure edit mode for the server-updated id is turned off
                setEditMode((prev) => ({ ...prev, [updated._id]: false, [id]: false }));
              } else {
                // no updated doc returned; still clear the saving flag for original id
                setSaving((prev) => {
                  const copy = { ...prev };
                  delete copy[id];
                  return copy;
                });

                setEditMode((prev) => ({ ...prev, [id]: false }));
              }
            } else {
              toast.error((ack && ack.message) || "Failed to save data");
              setSaving((prev) => {
                const copy = { ...prev };
                delete copy[id];
                return copy;
              });
            }
          });
        } catch (err) {
          console.error("socket.emit failed", err);
          toast.error("Failed to send save - see console");
          setSaving((prev) => {
            const copy = { ...prev };
            delete copy[id];
            return copy;
          });
        }
      } else {
        // Try a short reconnect flow if socket exists but is disconnected
        if (socket) {
          // ensure formattedData and authToken are in scope
          const payload = {
            outlineID: originalItem.outlineID,
            data: formattedData,
            authToken,
          };
          let reconnected = false;
          const tryReconnect = new Promise<boolean>((resolve) => {
            const onConnect = () => {
              reconnected = true;
              try {
                (socket as any).off("connect", onConnect);
              } catch {}
              resolve(true);
            };
            try {
              (socket as any).on("connect", onConnect);
            } catch {}
            try {
              // attempt to connect (no-op if already connecting)
              (socket as any).connect?.();
            } catch (e) {
              // ignore
            }
            // timeout after 3s
            setTimeout(() => {
              try {
                (socket as any).off("connect", onConnect);
              } catch {}
              resolve(reconnected);
            }, 3000);
          });

          tryReconnect.then((ok) => {
            if (ok && socket && (socket as any).connected) {
              // retry the save by emitting again
              (socket as any).emit("saveGeneratedData", payload, (ack: any) => {
                if (ack && ack.success) {
                  toast.success(ack.message || "Data saved successfully");
                  if (ack.data) {
                    const updated = ack.data;
                    setGeneratedData((prevData) =>
                      prevData.map((d) => (d._id === updated._id ? updated : d))
                    );
                    setEditData((prev) => ({
                      ...prev,
                      [updated._id]: updated,
                    }));

                    setSaving((prev) => {
                      const copy = { ...prev };
                      delete copy[id];
                      if (updated._id && updated._id !== id)
                        delete copy[updated._id];
                      return copy;
                    });
                  } else {
                    setSaving((prev) => {
                      const copy = { ...prev };
                      delete copy[id];
                      return copy;
                    });
                  }
                  toggleEditMode(id);
                } else {
                  toast.error(
                    (ack && ack.message) ||
                      "Failed to save data after reconnect"
                  );
                  setSaving((prev) => {
                    const copy = { ...prev };
                    delete copy[id];
                    return copy;
                  });
                }
              });
            } else {
              toast.error("Socket connection lost. Please refresh the page.");
              setSaving((prev) => {
                const copy = { ...prev };
                delete copy[id];
                return copy;
              });
            }
          });
        } else {
          toast.error("Socket connection lost. Please refresh the page.");
          setSaving((prev) => {
            const copy = { ...prev };
            delete copy[id];
            return copy;
          });
        }
      }
    },
    [socket, editData, toggleEditMode, generatedData]
  );

  // Cancel editing and reset data
  const cancelEdit = useCallback(
    (id: string) => {
      toggleEditMode(id);
      setEditData((prev) => {
        const newData = { ...prev };
        delete newData[id];
        return newData;
      });
    },
    [toggleEditMode]
  );

  // Function to scroll to a specific outline section with enhanced smooth scrolling
  const scrollToOutline = useCallback(
    (outlineID: string) => {
      if (!dataListRef.current) return;

      const sectionElement = document.getElementById(`section-${outlineID}`);
      if (sectionElement && dataListRef.current) {
        // Get the container's scrollTop position
        const containerTop = dataListRef.current.getBoundingClientRect().top;
        // Get section's position relative to the container
        const sectionTop =
          sectionElement.getBoundingClientRect().top - containerTop;

        // Use scrollTo for smoother animation
        dataListRef.current.scrollTo({
          top: dataListRef.current.scrollTop + sectionTop,
          behavior: "smooth",
        });

        // Also update the selected outline
        setSelectedOutlineID(outlineID);

        if (onOutlineChange) {
          onOutlineChange(outlineID);
        }
      }
    },
    [onOutlineChange]
  );

  // Enhanced scroll handler attachment with component stability
  useEffect(() => {
    if (!dataListRef.current) {
      return;
    }

    const handleScrollSafe = (e: Event) => {
      const target = e.target as HTMLDivElement;
      if (target && target.scrollTop !== undefined && !isAddingComponent.current) {
        lastScrollPosition.current = target.scrollTop;
      }
    };

    // Throttled scroll handler to prevent excessive updates
    const throttledScrollHandler = throttle(handleScrollSafe, 50);

    const currentRef = dataListRef.current;
    currentRef.addEventListener("scroll", throttledScrollHandler, { passive: true });

    return () => {
      if (currentRef) {
        currentRef.removeEventListener("scroll", throttledScrollHandler);
      }
    };
  }, []);

  return (
    <div
      className="dynamic-generated-data-viewer "
      style={{ perspective: "500px" }}
    >
      <div>
        {/* Main content area - full width */}
        <div className="w-full">
          {error && (
            <div className="error-message p-3 mb-4 bg-red-50 text-red-700 border border-red-200 rounded">
              Error: {error}
            </div>
          )}

          {loadingData && generatedData.length === 0 ? (
            <div className="flex justify-center items-center h-40">
              <div className="animate-pulse text-gray-500">
                Loading content...
              </div>
            </div>
          ) : generatedData.length > 0 ? (
            <div
              ref={dataListRef}
              className="data-list custom-scrollbar"
              style={{
                scrollbarWidth: "thin",
                overflowY: "auto",
                // Add properties for better component stability
                contain: "layout style", // Improve rendering performance
                willChange: "scroll-position", // Optimize for scrolling
                // Prevent layout shifts during component additions
                minHeight: "100%",
              }}
            >
              {/* Handle both single outline (desktop) and multiple outlines (mobile) with stability */}
              {Array.isArray(outlineData) ? (
                // Mobile view: render all outlines
                <div className="flex flex-col gap-3" style={{ contain: "layout" }}>
                  {outlineData.map((outline, index) => (
                    <div
                      key={`${outline.outlineID}-${index}`} // More stable key
                      id={`mobile-section-${outline.outlineID}`}
                      data-outline-id={outline.outlineID}
                      data-section-id={outline.outlineID}
                      className="scroll-section"
                      style={{
                        // Prevent layout shifts during component additions
                        contain: "layout style",
                        minHeight: "fit-content",
                        position: "relative"
                      }}
                    >
                      <SectionRenderer
                        key={`${outline.outlineID}-renderer-${index}`}
                        outlineSection={outline}
                        sectionData={generatedData.filter(
                          (item) => item?.outlineID === outline.outlineID
                        )}
                        editMode={editMode}
                        editData={editData}
                        toggleEditMode={toggleEditMode}
                        saveChanges={saveChanges}
                        cancelEdit={cancelEdit}
                        handleInputChange={handleInputChange}
                        saving={saving}
                        editable={editable}
                        handleRefineClick={handleRefineClick}
                        onAddNewOutline={onAddNewOutline}
                        onDeleteOutline={onDeleteOutline}
                        documentID={documentID}
                        onRefreshOutlines={onRefreshOutlines}
                        outlineIndex={index}
                      />
                    </div>
                  ))}
                </div>
              ) : outlineData ? (
                // Desktop view: render single outline
                <div
                  key={`${outlineData.outlineID}-desktop`}
                  data-section-id={outlineData.outlineID}
                  style={{
                    contain: "layout style",
                    minHeight: "fit-content",
                    position: "relative"
                  }}
                >
                  <SectionRenderer
                    key={`${outlineData.outlineID}-desktop-renderer`}
                    outlineSection={outlineData}
                    sectionData={generatedData.filter(
                      (item) => item?.outlineID === outlineData.outlineID
                    )}
                    editMode={editMode}
                    editData={editData}
                    toggleEditMode={toggleEditMode}
                    saveChanges={saveChanges}
                    cancelEdit={cancelEdit}
                    handleInputChange={handleInputChange}
                    saving={saving}
                    editable={editable}
                    handleRefineClick={handleRefineClick}
                    onAddNewOutline={onAddNewOutline}
                    onDeleteOutline={onDeleteOutline}
                    documentID={documentID}
                    onRefreshOutlines={onRefreshOutlines}
                    outlineIndex={idx}
                  />
                </div>
              ) : null}
            </div>
          ) : (
            <div className="flex justify-center items-center h-40">
              <div className="animate-pulse text-gray-500">
                Loading data...
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DynamicGeneratedDataViewer;
